
import { GoogleGenAI, Type } from "@google/genai";
import { AIAnalysis } from "../types";

/**
 * Generates an AI-powered personality analysis for a contestant.
 */
export const getContestantAnalysis = async (name: string, bio: string): Promise<AIAnalysis> => {
  try {
    // The Vite build will replace process.env.API_KEY. 
    // We use a local variable to satisfy TypeScript's strict checks.
    const apiKey = typeof process !== 'undefined' ? process.env.API_KEY : '';
    
    if (!apiKey) {
      console.warn("API Key is missing. Check your environment variables.");
    }

    const ai = new GoogleGenAI({ apiKey: apiKey || '' });
    
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `Analyze this Splitsvilla contestant based on her intro: 
      Name: ${name}
      Intro/Bio: ${bio}
      
      Generate a fun, reality-show style analysis.`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            personalityTraits: {
              type: Type.ARRAY,
              items: { type: Type.STRING },
              description: "Three keywords for her personality"
            },
            dramaScore: {
              type: Type.NUMBER,
              description: "A score from 1 to 10 on potential drama quotient"
            },
            vibes: {
              type: Type.STRING,
              description: "The 'vibe' she brings to the villa (e.g., Sweetheart, Mastermind)"
            },
            summary: {
              type: Type.STRING,
              description: "A short sassy prediction for her season"
            }
          },
          required: ["personalityTraits", "dramaScore", "vibes", "summary"]
        }
      }
    });

    const result = response.text;
    if (!result) throw new Error("Empty response from AI");
    
    return JSON.parse(result.trim()) as AIAnalysis;
  } catch (error) {
    console.error("AI Analysis Error:", error);
    return {
      personalityTraits: ["Iconic", "Confident", "Bold"],
      dramaScore: 8,
      vibes: "The Main Character",
      summary: "She's already the talk of the town. Expect fireworks and high-stakes gameplay!"
    };
  }
};
